import { submitFormJson } from 'widgets/toolbox/ajax';
import { getStateID, isStateRestricted } from 'harmony/toolbox/shipToUtils';
import { isMediumViewAndDown } from 'widgets/toolbox/viewtype';

/**
 * @description Base HarmonyAddToCartMixin implementation
 * <br>This class is not intended to have a separate DOM representation.
 * @param AddToCartMixin
 * @param {ProductDetail} ProductDetail Base widget for extending
 * @returns {typeof HarmonyAddToCartMixin} Harmony Add to Cart mixin class
 */
export default function (AddToCartMixin) {
    /**
     * @class HarmonyAddToCartMixin
     * @augments AddToCartMixin
     * @property {string} data-text-network-error - network error text
     * @property {string} data-add-to-cart-label - add to cart label
     * @property {string} data-pid - product id
     * @property {string} data-show-minicart-on-product-add - Show minicart on product add
     * @property {string} data-show-message-on-product-add - Show message on poroduct add
     */
    class HarmonyAddToCartMixin extends AddToCartMixin {
        prefs() {
            return {
                ...super.prefs(),
                autoCloseMinicart: false,
                autoCloseTimeout: 5000,
                showMessageOnProductAdd: false,
                classHidden: 'h-hidden',
                shipToRestrictedStates: [],
                isHandledRestrictedStates: false,
                isDisabled: false
            };
        }

        get constants() {
            return {
                INPUT_QUANTITY_ELEMENT_ID: 'input-quantity',
                ALERT_SHOW: 'alert.show'
            };
        }

        init() {
            super.init();

            // handle shipping restrictions
            this.eventBus().on('shipto.restrictions.updated', 'handleStateRestrictions');
            this.eventBus().on('shipto.restrictions.updated.addtocart', 'handleStateRestrictions');
            this.eventBus().on('minicart.removed.product', 'handleRemovedProduct');
            this.handleStateRestrictions();

            this.buttonText = '';
        }

        /**
         * @description Get value from input quantity block
         */
        getQtyValue() {
            return this.getById(this.constants.INPUT_QUANTITY_ELEMENT_ID, (qtyInput) => {
                return (qtyInput && qtyInput.ref('field') && qtyInput.ref('field').val());
            });
        }

        /**
         * @description get value from button data attribute
         */
        getChildProducts() {
            return this.getById('addToCart', (button) => {
                return JSON.stringify((button && button.config && button.config.childProducts));
            });
        }

        /**
         * @description handle response from server after product added
         * @param {any} response Add Product response
         */
        postAddProduct(response) {
            let pid = document.getElementById('add-to-cart-product-id');
            const productId = pid?.getAttribute('data-pid');
            let element = document.getElementById('add-to-cart-' + productId);
            if (!response.error && response.cart) {
                this.emit('productadded');
                const cartModel = response.cart;
                // Hide mini cart pop up for CSC Agent
                if (response.isCSCAgent) {
                    cartModel.showMinicart = false;
                } else {
                    cartModel.showMinicart = this.prefs().showMinicartOnProductAdd;
                }
                element?.setAttribute('data-add-to-cart-success', 'true');
                this.eventBus().emit('product.added.to.cart', cartModel, this);
                this.showAlertMessage(this.prefs().accessibilityAlerts.addedtocart);
                    this.updateAvailabilityComponent();
            }

            const { autoCloseMinicart, autoCloseTimeout } = this.prefs();
            if (!response.error && response.cart && autoCloseMinicart) {
                this.eventBus().emit('auto.close.minicart', autoCloseTimeout);
            }

            if (response.error || this.prefs().showMessageOnProductAdd) {
                element?.setAttribute('data-add-to-cart-success', 'false');
                const accessibilityAlert = response.message;

                this.eventBus().emit('product.addedTocart.with.error', response);
                this.eventBus().emit(this.constants.ALERT_SHOW, {
                    accessibilityAlert,
                    errorClass: true
                });
            }

            // set out of stock status
            if (response.isOutOfStock && response.errorData) {
                // set max available value
                this.getById(this.constants.INPUT_QUANTITY_ELEMENT_ID, (quantity) => {
                    quantity.ref('field').val(response.errorData.qtyValue);
                });

                // disable `Add to cart` button
                this.getById('addToCart', (addToCartBtn) => {
                    addToCartBtn.setText(this.prefs().outOfStockLabel);
                    addToCartBtn.disable();
                    this.buttonText = this.prefs().outOfStockLabel;
                });

                // disable quantity's block
                this.eventBus().emit('product.quantity.disable', true);

                // set availability
                this.getById('availability', (availability) => {
                    availability.render('template', response.errorData, availability.ref('container'));
                });
                element?.setAttribute('data-add-to-cart-success', 'false');
                const outOfStockError = this.prefs().accessibilityAlerts.outofstock;
                this.eventBus().emit(this.constants.ALERT_SHOW, {
                    accessibilityAlert: outOfStockError,
                    errorClass: true
                });
            }

            // set low in stock status
            if (response.isNotEnoughATS && response.errorData) {
                // set max available value
                this.getById(this.constants.INPUT_QUANTITY_ELEMENT_ID, (quantity) => {
                    quantity.ref('field').val(response.errorData.qtyValue);
                });

                // set availability
                this.getById('availability', (availability) => {
                    availability.render('template', response.errorData, availability.ref('container'));
                });
            }
        }

        /**
         * @description widget triggered event
         * Run `addToCart` event
         * @param {object} button
         */
        addToCart(button) {
            this.eventBus().emit('addtocart.button.clicked', true);

            // Prevent twice clicks on a button
            if (button.isBusy) {
                return;
            }

            const addToCartBtnPrefs = button.prefs();

            if (addToCartBtnPrefs.readyToOrder) {
                this.readyToOrder = addToCartBtnPrefs.readyToOrder;
            }

            if (!this.readyToOrder) {
                this.showCartMessage(this.prefs().textSelectOptions, true);

                return;
            }

            if (this.stock && this.stock < this.selectedQuantity) {
                this.showCartMessage(this.prefs().textStockLimit, true);

                return;
            }

            button.startProcess();

            this.showProgressBar();

            const promise = submitFormJson(addToCartBtnPrefs.addToCartUrl, {
                pid: this.currentProductID || addToCartBtnPrefs.pid,
                quantity: this.getQtyValue() || this.selectedQuantity || 1,
                childProducts: this.getChildProducts() || []
            }).then(response => this.postAddProduct(response))
                .finally(() => {
                    button.stopProcess(this.buttonText);
                    this.buttonText = '';

                    this.hideProgressBar();

                    this.eventBus().emit('addtocart.button.clicked', false);
                }).catch(() => {
                    //once bundle perfomance issue is fixed, Remove if condition.
                    if(addToCartBtnPrefs.isbundle === undefined && !addToCartBtnPrefs.isbundle){
                        this.showCartMessage(this.prefs().textNetworkError, true);
                    }
                });

            this.eventBus().emit('loader.start', promise);
        }

        /**
         * @description Toggle disable attr of addToCartBtn
         * @param {any} disabled
         */
        toggleDisable(disabled) {
            this.getById('addToCart', (processButton) => {
                if (disabled) {
                    processButton.disable();
                } else {
                    processButton.enable();
                }
            });
        }

        /**
         * @description Render `Add To Cart` button
         * @param {object} product
         */
        renderAddToCart(product) {
            this.getById('addToCart', (addToCartBtn) => {
                if (product.shipToRestricted || product.isEmptyPrice) {
                    addToCartBtn.disable();
                } else if (!product.available && product.availability.isOutOfStock) {
                    addToCartBtn.setText(this.prefs().outOfStockLabel);
                    addToCartBtn.disable();
                } else if (product.availability.isPreorder) {
                    addToCartBtn.setText(this.prefs().preorderLabel);
                    addToCartBtn.enable();
                } else {
                    addToCartBtn.setText(this.prefs()[this.prefs().update ? 'updateLabel' : 'addToCartLabel']);
                    addToCartBtn.enable();
                }
            });
        }

        /**
         * @description Add to cart trigger click handler
         */
        triggerClick() {
            this.getById('addToCart', (processButton) => {
                this.addToCart(processButton);
            });
        }

        /**
         * @description Toggle busy of addToCartBtn
         * @param {object} initiator - widget initiator
         * @param {boolean} isBusy - is busy processing flag
         */
        toggleBusy(initiator, isBusy) {
            this.getById('addToCart', (processButton) => {
                if (isBusy) {
                    processButton.startProcess();
                } else {
                    processButton.stopProcess();
                }
            });
        }

        /**
         * @description Emits global alert
         * @param {string} message Text for an alert
         * @param {boolean} isError is error flag
         */
        showAlertMessage(message, isError = false) {
            this.eventBus().emit(this.constants.ALERT_SHOW, {
                accessibilityAlert: message,
                errorClass: isError
            });
        }

        /**
         * @param {any} stateID - state id
         * @param {boolean|undefined} forceRestriction - force restriction
         * @description Handle update of shipping restrictions
         */
        handleStateRestrictions(stateID, forceRestriction) {
            var addToCartBtn = this.getById('addToCart', (btn) => {
                return btn;
            });

            if (!addToCartBtn || !addToCartBtn.prefs() || addToCartBtn.prefs().isDisabled
                    || !addToCartBtn.prefs().isHandledRestrictedStates) {
                return;
            }

            stateID = getStateID(stateID);
            console.log('btn', stateID);
            if (!stateID) {
                return;
            }

            var isRestricted = forceRestriction || isStateRestricted(stateID, addToCartBtn.prefs().shipToRestrictedStates);
            var classHidden = this.prefs().classHidden;

            // handle `addToCart` button
            this.toggleDisable(isRestricted);
            addToCartBtn.ref('self').toggleClass(classHidden, isRestricted);

            // handle disabled button
            this.getById('product_disabledbtn', (disabledBtn) => {
                disabledBtn.ref('self').toggleClass(classHidden, !isRestricted);
            });
        }

        /**
         * @description handle removed product
         * @param {string} pid - product id
         */
        handleRemovedProduct(pid) {
            if (pid === this.pid) {
                this.updateAvailabilityComponent();
            }
        }

        /**
         * @description update availability component
         */
        updateAvailabilityComponent() {
             // eslint-disable-next-line max-len
             const HarmonyQuantityStepper = /** @type {HarmonyQuantityStepper} */(this.getConstructor('harmonyQuantityStepper'));

             this.eachChild(child => {
                 if ((child instanceof HarmonyQuantityStepper)) {
                     child.changeValue();
                 }
             });
        }
    }

    return HarmonyAddToCartMixin;
}
